import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { ProjectOption } from "../lib/projects";

// Shared CRUD dialog for glossary terms. Consumed by ProjectDetail's registry
// sections. Mirrors AttendeeFormDialog.
// Under the "purest model" (2026-05-31) a project is REQUIRED — there is no
// workspace-global option.

export interface GlossaryRow {
  id: string;
  workspace_id: string;
  project_id: string | null;
  term: string;
  notes: string | null;
  aliases: string[];
}

interface Draft {
  project_id: string | null;
  term: string;
  notes: string;
  aliases_input: string;
}

function formatAliases(arr: string[] | null | undefined): string {
  return (arr ?? []).join(", ");
}

function parseAliases(input: string): string[] {
  return input
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function draftFromRow(row: GlossaryRow): Draft {
  return {
    project_id: row.project_id,
    term: row.term,
    notes: row.notes ?? "",
    aliases_input: formatAliases(row.aliases),
  };
}

function emptyDraft(defaultProjectId: string | null): Draft {
  return {
    project_id: defaultProjectId,
    term: "",
    notes: "",
    aliases_input: "",
  };
}

export interface GlossaryFormDialogProps {
  state:
    | { mode: "create"; defaultProjectId: string | null }
    | { mode: "edit"; row: GlossaryRow }
    | null;
  workspaceId: string;
  projects: ProjectOption[];
  onClose: () => void;
  onSaved: () => void;
}

export function GlossaryFormDialog({
  state,
  workspaceId,
  projects,
  onClose,
  onSaved,
}: GlossaryFormDialogProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    if (state === null) {
      setDraft(null);
      setSaveError(null);
      return;
    }
    if (state.mode === "create") {
      setDraft(emptyDraft(state.defaultProjectId));
    } else {
      setDraft(draftFromRow(state.row));
    }
    setSaveError(null);
  }, [state]);

  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (state && !dlg.open) dlg.showModal();
    if (!state && dlg.open) dlg.close();
  }, [state]);

  function close() {
    if (saving) return;
    onClose();
  }

  async function save() {
    if (!draft || !state) return;
    const trimmedTerm = draft.term.trim();
    if (!trimmedTerm) {
      setSaveError("Term is required.");
      return;
    }
    if (!draft.project_id) {
      setSaveError("A project is required. Select a project for this term.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    const payload = {
      workspace_id: workspaceId,
      project_id: draft.project_id,
      term: trimmedTerm,
      notes: draft.notes.trim() || null,
      aliases: parseAliases(draft.aliases_input),
    };
    let err: { code?: string; message: string } | null = null;
    if (state.mode === "create") {
      const { error: e } = await supabase
        .from("glossary_terms")
        .insert(payload);
      err = e;
    } else {
      const { error: e } = await supabase
        .from("glossary_terms")
        .update(payload)
        .eq("id", state.row.id);
      err = e;
    }
    setSaving(false);
    if (err) {
      if (err.code === "23505") {
        setSaveError(
          `A glossary term "${trimmedTerm}" already exists in this workspace. ` +
            `Close this dialog and edit the existing row, or pick a different term.`,
        );
      } else {
        setSaveError(err.message);
      }
      return;
    }
    onSaved();
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={close}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        background: "var(--surface)",
        color: "var(--text)",
        padding: 20,
        minWidth: 480,
        maxWidth: "min(640px, 90vw)",
      }}
    >
      {state && draft && (
        <form
          method="dialog"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            {state.mode === "create"
              ? "Add glossary term"
              : "Edit glossary term"}
          </h3>
          <div className="field">
            <label htmlFor="glo-term">Term</label>
            <input
              id="glo-term"
              type="text"
              dir="auto"
              autoFocus
              value={draft.term}
              onChange={(e) => setDraft({ ...draft, term: e.target.value })}
              disabled={saving}
              placeholder="e.g. DHL"
            />
          </div>
          <div className="field">
            <label htmlFor="glo-project">Project</label>
            <select
              id="glo-project"
              value={draft.project_id ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  project_id: e.target.value === "" ? null : e.target.value,
                })
              }
              disabled={saving}
              style={{
                padding: "5px 8px",
                background: "var(--surface)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: 4,
                fontSize: 13,
                width: "100%",
              }}
            >
              <option value="">— Select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <div
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                marginTop: 4,
              }}
            >
              Flows into meetings in this project only. A project is required.
            </div>
          </div>
          <div className="field">
            <label htmlFor="glo-aliases">Aliases (comma-separated)</label>
            <input
              id="glo-aliases"
              type="text"
              dir="auto"
              value={draft.aliases_input}
              onChange={(e) =>
                setDraft({ ...draft, aliases_input: e.target.value })
              }
              disabled={saving}
              placeholder="e.g. די.אייץ.אל, DHL Express"
            />
            <div
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                marginTop: 4,
              }}
            >
              Alternate spellings. Hebrew/English variants of the same
              term. Claude sees them all when reviewing transcripts.
            </div>
          </div>
          <div className="field">
            <label htmlFor="glo-notes">Notes (optional)</label>
            <textarea
              id="glo-notes"
              dir="auto"
              rows={3}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              disabled={saving}
              placeholder="e.g. International courier company; comes up in shipping discussions"
            />
          </div>
          {saveError && (
            <p className="error" style={{ fontSize: 13, marginBlock: 8 }}>
              {saveError}
            </p>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 12,
            }}
          >
            <button
              type="button"
              className="secondary"
              onClick={close}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" disabled={saving}>
              {saving ? "Saving…" : state.mode === "create" ? "Add" : "Save"}
            </button>
          </div>
        </form>
      )}
    </dialog>
  );
}
